(() => {
  'use strict';

  const REQUEST_KEY = '__titanVariantPriceRequest';
  const INIT_KEY = 'titanVariantFixV2';
  const escapeCss = (value) => {
    const text = String(value ?? '');
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(text);
    return text.replace(/([\\"'\[\]#.:,>+~*=() ])/g, '\\$1');
  };

  const decodeOption = (value) => String(value ?? '')
    .split('Special_Double_Quote').join('"')
    .split('Special_Slash').join('/');

  const getRoot = (picker) =>
    picker?.closest?.('.productView') ||
    picker?.closest?.('[id^="ProductSection-"]') ||
    document;

  const getVariants = (picker) => {
    try {
      const json = picker?.querySelector?.('script[type="application/json"]');
      return json ? JSON.parse(json.textContent) : [];
    } catch (error) {
      console.error('[Titan Variant Fix V2] Variant JSON error:', error);
      return [];
    }
  };

  const selectedOptions = (picker) => {
    if (!picker) return [];

    if (picker.matches('variant-radios')) {
      return Array.from(picker.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return decodeOption(checked?.value);
      });
    }

    return Array.from(picker.querySelectorAll('select')).map((select) => decodeOption(select.value));
  };

  const resolveVariant = (picker) => {
    const options = selectedOptions(picker);
    if (!options.length) return picker?.currentVariant || null;

    const found = getVariants(picker).find((variant) =>
      Array.isArray(variant.options) &&
      variant.options.every((option, index) => decodeOption(option) === options[index])
    );

    return found || picker?.currentVariant || null;
  };

  const money = (cents) => {
    const amount = Number(cents || 0);
    try {
      if (window.Shopify?.formatMoney && window.money_format) {
        return window.Shopify.formatMoney(amount, window.money_format);
      }
    } catch (_) {}

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.shop_currency || 'USD'
      }).format(amount / 100);
    } catch (_) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  const applyLocalPrice = (picker, variant) => {
    if (!picker || !variant) return;

    const productId = picker.dataset.product;
    const root = getRoot(picker);
    const priceRoot = root.querySelector(`#product-price-${escapeCss(productId)}`) ||
      document.getElementById(`product-price-${productId}`);
    if (!priceRoot) return;

    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    const onSale = compareAt > price;
    const priceText = money(price);
    const compareText = onSale ? money(compareAt) : '';

    priceRoot.querySelectorAll('.price__regular .price-item--regular').forEach((node) => {
      node.textContent = priceText;
    });
    priceRoot.querySelectorAll('.price__sale .price-item--sale').forEach((node) => {
      node.textContent = priceText;
    });
    priceRoot.querySelectorAll('.price__compare .price-item--regular').forEach((node) => {
      node.textContent = compareText;
    });

    const priceBox = priceRoot.querySelector('.price');
    if (priceBox) {
      priceBox.classList.toggle('price--on-sale', onSale);
      priceBox.classList.toggle('price--sold-out', variant.available === false);
    }

    const compareNode = priceRoot.querySelector('.price__compare');
    if (compareNode) compareNode.dataset.compare = String(onSale ? compareAt : 0);

    const lastNode = priceRoot.querySelector('.price__sale .price__last');
    if (lastNode) lastNode.dataset.last = String(price);

    priceRoot.querySelectorAll('.label_sale_percent').forEach((node) => {
      if (!onSale || !compareAt) {
        node.textContent = '';
        return;
      }
      const percent = Math.round(((compareAt - price) / compareAt) * 100);
      node.textContent = `(-${percent}%)`;
    });

    priceRoot.classList.remove('visibility-hidden');
  };

  const refreshPriceFromShopify = (picker, variant) => {
    if (!picker || !variant?.id || !picker.dataset.url || !picker.dataset.section) return;

    const requestId = String(variant.id);
    picker[REQUEST_KEY] = requestId;

    const url = new URL(picker.dataset.url, window.location.origin);
    url.searchParams.set('variant', requestId);
    url.searchParams.set('section_id', picker.dataset.section);

    fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const current = resolveVariant(picker);
        if (picker[REQUEST_KEY] !== requestId || String(current?.id) !== requestId) return;

        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const id = `product-price-${picker.dataset.product}`;
        const source = parsed.getElementById(id);
        const root = getRoot(picker);
        const destination = root.querySelector(`#${escapeCss(id)}`) || document.getElementById(id);

        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.classList.remove('visibility-hidden');
        } else {
          applyLocalPrice(picker, current || variant);
        }
      })
      .catch(() => {
        const current = resolveVariant(picker);
        if (String(current?.id) === requestId) applyLocalPrice(picker, current || variant);
      });
  };

  const disableEllaImageGrouping = (root) => {
    window.variant_image_group = false;

    (root || document).querySelectorAll('.enable_variant_image_group').forEach((node) => {
      node.classList.remove('enable_variant_image_group');
    });

    (root || document).querySelectorAll('.productView-nav.media-filter, .productView-for.media-filter').forEach((node) => {
      node.classList.remove('media-filter');
    });

    (root || document).querySelectorAll('variant-radios [data-filter], variant-selects [data-filter]').forEach((node) => {
      node.removeAttribute('data-filter');
    });
  };

  const getMediaId = (variant) => {
    const media = variant?.featured_media || variant?.featured_image || variant?.image;
    if (!media) return null;
    const id = media.id ?? media.media_id;
    if (id == null) return null;
    return String(id).replace(/^gid:\/\/shopify\/(?:MediaImage|ProductImage)\//, '');
  };

  const getMediaSrc = (variant) => {
    const media = variant?.featured_media || variant?.featured_image || variant?.image;
    return media?.preview_image?.src || media?.src || null;
  };

  const activateVariantMedia = (picker, variant) => {
    const mediaId = getMediaId(variant);
    if (!picker || !mediaId) return false;

    const root = getRoot(picker);
    disableEllaImageGrouping(root);

    const nav = root.querySelector('.productView-nav');
    if (!nav) return false;

    const mediaContainer = root.querySelector(`.product-single__media[data-media-id="${escapeCss(mediaId)}"]`);
    const targetSlide = mediaContainer?.closest('.productView-image');

    if (targetSlide && window.jQuery) {
      const $nav = window.jQuery(nav);
      if ($nav.hasClass('slick-initialized') && typeof $nav.slick === 'function') {
        let index = Number(targetSlide.getAttribute('data-slick-index'));

        if (!Number.isFinite(index)) {
          const originalSlides = Array.from(nav.children).filter((node) =>
            node.classList?.contains('productView-image') && !node.classList.contains('slick-cloned')
          );
          index = originalSlides.indexOf(targetSlide);
        }

        if (Number.isFinite(index) && index >= 0) {
          try {
            $nav.slick('slickGoTo', index, false);
            return true;
          } catch (_) {}
        }
      }
    }

    if (targetSlide) {
      const firstSlide = Array.from(nav.children).find((node) => node.classList?.contains('productView-image'));
      if (firstSlide && firstSlide !== targetSlide) {
        nav.insertBefore(targetSlide, firstSlide);
      }
      targetSlide.classList.add('titan-variant-media-active');
      return true;
    }

    const src = getMediaSrc(variant);
    if (!src) return false;

    const image = nav.querySelector('.slick-current img[data-main-image], img[data-main-image]');
    if (!image) return false;

    image.src = src;
    image.srcset = src;
    image.dataset.src = src;
    image.dataset.srcset = src;
    const zoom = image.closest('.media');
    if (zoom?.hasAttribute('data-zoom-image')) zoom.setAttribute('data-zoom-image', src);
    return true;
  };

  const syncVariant = (picker) => {
    if (!picker) return;
    const variant = resolveVariant(picker);
    if (!variant) return;

    // Keep Ella's internal selected variant aligned with the actual controls.
    picker.currentVariant = variant;

    applyLocalPrice(picker, variant);
    refreshPriceFromShopify(picker, variant);

    [0, 100, 350, 700].forEach((delay) => {
      window.setTimeout(() => {
        const current = resolveVariant(picker);
        if (String(current?.id) === String(variant.id)) {
          activateVariantMedia(picker, current);
        }
      }, delay);
    });
  };

  const init = () => {
    disableEllaImageGrouping(document);

    document.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
      if (!picker.dataset[INIT_KEY]) picker.dataset[INIT_KEY] = 'true';
      syncVariant(picker);
    });
  };

  document.addEventListener('change', (event) => {
    const picker = event.target?.closest?.('variant-radios, variant-selects');
    if (!picker) return;
    window.setTimeout(() => syncVariant(picker), 0);
  }, true);

  document.addEventListener('click', (event) => {
    const label = event.target?.closest?.('variant-radios label[for]');
    if (!label) return;
    const picker = label.closest('variant-radios');
    if (!picker) return;
    window.setTimeout(() => syncVariant(picker), 30);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('load', init, { once: true });
  document.addEventListener('shopify:section:load', init);
})();
